import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrNodes } from '@/api/ivrNodes';
import { ivrEdges } from '@/api/ivrEdges';
import type { IvrNodeType } from '@/api/ivrNodes';
import { IVR_NODE_TYPE_GROUPS } from '@/api/ivrNodes';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from '@/components/ui/select';
import { X } from 'lucide-react';

interface Props {
  treeId: string;
  fromNodeId: string | null; // null = no parent, just create a floating node
  defaultBranchKey?: string;
  /** Initial position to spawn new node (canvas coords) */
  spawnX?: number;
  spawnY?: number;
  onCreated: (newNodeId: string) => void;
  onClose: () => void;
  /** Screen coordinates for popover placement */
  screenX?: number;
  screenY?: number;
}

/**
 * Map node types to the feature flag that gates them. Missing entries mean
 * the node type doesn't require any feature flag (always available). Super
 * admin bypasses this filter via the useFeatureFlag hook.
 */
const NODE_TYPE_FEATURE_MAP: Partial<Record<IvrNodeType, string>> = {
  voicemail: 'voicemail',
  listen_voicemail: 'voicemail',
  conference: 'conference',
  survey: 'surveys',
  quiz: 'quizzes',
  enrollment: 'courses',
  shopping: 'shop',
  points: 'points',
  public_forum: 'public_forum',
  ring_group: 'ring_groups',
  payment: 'payments',
  extension: 'extensions',
};

export function CreateNodePopover({
  treeId,
  fromNodeId,
  defaultBranchKey = 'next',
  spawnX = 0,
  spawnY = 200,
  onCreated,
  onClose,
  screenX = 400,
  screenY = 300,
}: Props) {
  const qc = useQueryClient();
  const nameRef = useRef<HTMLInputElement>(null);
  const { isEnabled } = useFeatureFlag();

  const [name, setName] = useState('');
  const [nodeType, setNodeType] = useState<IvrNodeType>('playback');
  const [branchKey, setBranchKey] = useState(defaultBranchKey);
  const [error, setError] = useState('');

  // Filter node-type groups by feature flags. Super admin bypasses via the
  // hook so all types remain available even when the flag is disabled.
  const availableGroups = useMemo(() => {
    return IVR_NODE_TYPE_GROUPS
      .map((g) => ({
        ...g,
        types: g.types.filter((t) => {
          const flag = NODE_TYPE_FEATURE_MAP[t.type];
          return !flag || isEnabled(flag);
        }),
      }))
      .filter((g) => g.types.length > 0);
  }, [isEnabled]);

  // Auto-focus name
  useEffect(() => {
    setTimeout(() => nameRef.current?.focus(), 50);
  }, []);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const create = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name is required');
      if (fromNodeId && !branchKey.trim()) throw new Error('Branch key is required');

      // Create the node
      const newNode = await ivrNodes.create(treeId, {
        node_type: nodeType,
        name: name.trim(),
        config: {},
      });

      // Patch position
      await ivrNodes.update(newNode.id, {
        position_x: spawnX,
        position_y: spawnY,
      });

      // Create edge if from a parent
      if (fromNodeId) {
        await ivrEdges.create(fromNodeId, {
          branch_key: branchKey.trim(),
          to_node_id: newNode.id,
        });
      }

      return newNode;
    },
    onSuccess: (node) => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      onCreated(node.id);
      onClose();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Failed to create node');
    },
  });

  const panelW = 300;
  const clampedX = Math.min(Math.max(screenX, 8), window.innerWidth - panelW - 8);
  const clampedY = Math.min(Math.max(screenY, 8), window.innerHeight - 300 - 8);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      <div
        className="fixed z-50 w-[300px] rounded-lg border border-slate-200 bg-white shadow-xl"
        style={{ left: clampedX, top: clampedY }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5">
          <p className="text-xs font-semibold text-slate-700">
            {fromNodeId ? 'Add child node' : 'Create node'}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <form
          className="px-3 py-3 space-y-3"
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        >
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="cnp-name" className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Name
            </Label>
            <Input
              ref={nameRef}
              id="cnp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Main Menu"
              className="h-8 text-sm"
              maxLength={200}
            />
          </div>

          {/* Node type */}
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Type
            </Label>
            <Select value={nodeType} onValueChange={(v) => setNodeType(v as IvrNodeType)}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {availableGroups.map((group) => (
                  <SelectGroup key={group.label}>
                    <SelectLabel className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 px-2 py-1">
                      {group.label}
                    </SelectLabel>
                    {group.types.map((t) => (
                      <SelectItem key={t.type} value={t.type}>
                        <span className="flex items-baseline gap-2">
                          <span className="text-sm">{t.label}</span>
                          <span className="font-mono text-[10px] text-slate-400">{t.type}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const hint = availableGroups
                .flatMap((g) => g.types)
                .find((t) => t.type === nodeType)?.hint;
              return hint ? (
                <p className="text-[10px] text-slate-400 mt-1">{hint}</p>
              ) : null;
            })()}
          </div>

          {/* Branch key (only when has parent) */}
          {fromNodeId && (
            <div className="space-y-1">
              <Label htmlFor="cnp-bk" className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Branch key
              </Label>
              <Input
                id="cnp-bk"
                value={branchKey}
                onChange={(e) => setBranchKey(e.target.value)}
                placeholder="next, 1, 2, timeout…"
                className="h-8 text-sm font-mono"
                maxLength={32}
              />
              <p className="text-[10px] text-slate-400">
                How this node is reached from the parent.
              </p>
            </div>
          )}

          {error && <p className="text-[11px] text-red-500">{error}</p>}

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 h-8 text-xs"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="flex-1 h-8 text-xs"
              disabled={!name.trim() || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
